import {
    CartItemViewModel,
    CartViewModel,
    GetPaymentOptionsViewModel,
    GetSubCartViewModel,
    PaymentFinalStatusViewModel,
    ProductViewModel,
    PromotionOfferType,
} from '@/types/api'
import { GetShippingOptionsViewModel, ShippingOptionDetailViewModel } from '@/types/nrc-api'

import { PagePathStorageKey } from '@/components/header/utils/types'

import { PUBLIC_DOMAIN_URL } from '@/app/_constants/urls'
import { normalizeDestinationUrl } from '@/app/_utils/normalize-destination-url'

import {
    ActionType,
    CartActionPlacement,
    CartProductType,
    KeyPathType,
    PdpType,
    SubmitedReviewType,
    SupportedEvents,
    TrackPageViewType,
    TrackingDataType,
    WebengageEventPayloadMap,
} from './types'
import { generateBreadcrumbs, productNameSizeHandler, sendWebengageEvent } from './utils'

import { calculatePurchaseTotalValue } from './utils/calculate-total-value'
import { calculateVariantsTotalPrice } from './utils/calculate-variants-total-price'
import { generateProductVariants } from './utils/generate-product-data'

import { ViewItemType } from '../analytics/datalayer/types'
import { generateEventsCouponData } from '../analytics/datalayer/utils'

export const trackPageView = ({ pathname, pageTitle, prevPath }: TrackPageViewType) => {
    const trackerType = pathname === '/' ? SupportedEvents.homepage_view : SupportedEvents.page_view

    const trackingData: TrackingDataType = {
        page_url: `${PUBLIC_DOMAIN_URL}${pathname}`,
        ...(pageTitle && pageTitle !== 'undefined undefined' ? { page_title: pageTitle } : {}),
        ...(prevPath !== PagePathStorageKey.Base ? { page_referrer: prevPath } : {}),
    }

    sendWebengageEvent(trackerType, trackingData)
}

export const trackSearch = (query: string | string[]) => {
    sendWebengageEvent(SupportedEvents.search, {
        search_term: query,
    })
}

export const trackViewPdp = (pdpData: ProductViewModel, type: ViewItemType) => {
    const variantFields = generateProductVariants(pdpData, null, null, null, [
        KeyPathType.id,
        KeyPathType.salesPrice,
        KeyPathType.discountPrice,
        KeyPathType.innerColorName,
        KeyPathType.shipBySeller,
    ])

    const updatedViewPdpData: PdpType = {
        item_brand: pdpData?.brand?.nameFa ?? '',
        item_name: productNameSizeHandler(pdpData?.nameFa ?? ''),
        item_id: variantFields[KeyPathType.id],
        price: variantFields[KeyPathType.salesPrice],
        value: calculateVariantsTotalPrice(variantFields[KeyPathType.salesPrice]),
        discount: variantFields[KeyPathType.discountPrice],
        item_list_id: type,
        item_list_name: type,
        ship_by_seller: variantFields[KeyPathType.shipBySeller],
        ...(variantFields[KeyPathType.innerColorName] !== 'Blank'
            ? { item_variant: variantFields[KeyPathType.innerColorName] }
            : {}),
        ...generateBreadcrumbs(pdpData),
    }

    sendWebengageEvent(SupportedEvents.view_item, updatedViewPdpData)
}

export const trackReviewSubmit = (productData: ProductViewModel, rate: string) => {
    const updatedSubmitedReviewData: SubmitedReviewType = {
        product_id: productData?.id ?? '',
        product_name: productData?.nameFa ?? '',
        item_brand: productData?.brand?.nameFa ?? '',
        rating: Number(rate),
        ...generateBreadcrumbs(productData),
    }

    sendWebengageEvent(SupportedEvents.product_review_submitted, updatedSubmitedReviewData)
}

export const trackCouponCodeFailure = (coupon_code: string, failure_reason: string) => {
    sendWebengageEvent(SupportedEvents.coupon_code_failed, {
        coupon_code,
        failure_reason,
    })
}

export const trackCartView = (cart: CartViewModel) => {
    const variantFields = generateProductVariants(null, cart, null, null, [
        KeyPathType.atpId,
        KeyPathType.productName,
        KeyPathType.salesPrice,
        KeyPathType.discount,
        KeyPathType.brandName,
        KeyPathType.colorName,
        KeyPathType.categoryLevel1,
        KeyPathType.categoryLevel2,
        KeyPathType.categoryLevel3,
        KeyPathType.shipBySeller,
    ])

    const basketData = {
        value: cart.totalEffectivePrice ?? 0,
        overall_quantity: cart.sumOfQty ?? 0,
        item_id: variantFields[KeyPathType.atpId] ?? '',
        item_name: variantFields[KeyPathType.productName] ?? '',
        price: variantFields[KeyPathType.salesPrice],
        discount: variantFields[KeyPathType.discount],
        item_brand: variantFields[KeyPathType.brandName] ?? '',
        item_category1: variantFields[KeyPathType.categoryLevel1] ?? '',
        item_category2: variantFields[KeyPathType.categoryLevel2] ?? '',
        item_category3: variantFields[KeyPathType.categoryLevel3] ?? 'Blank',
        ship_by_seller: variantFields[KeyPathType.shipBySeller] ?? false,
        ...(variantFields[KeyPathType.colorName] !== 'Blank'
            ? { item_variant: variantFields[KeyPathType.colorName] }
            : {}),
    }

    sendWebengageEvent(SupportedEvents.view_cart, basketData)
}

export const trackShippingInfo = (
    cart: GetSubCartViewModel,
    shipmentCount: number,
    shippingCostWithDiscountCalculation: number,
    khanoumiSubOrderShippingMethod?: GetShippingOptionsViewModel,
    khanoumiSubOrderShippingCost?: ShippingOptionDetailViewModel
) => {
    const hasFreeShipping = cart.promotion?.offerType === PromotionOfferType.FreeShipping

    const shippingInfo: WebengageEventPayloadMap[SupportedEvents.add_shipping_info] = {
        shipment_count: shipmentCount,
        shipping_price: shippingCostWithDiscountCalculation,
        delivery_date: khanoumiSubOrderShippingMethod?.shippingMethodTitle?.includes('پست')
            ? new Date(khanoumiSubOrderShippingCost?.toDay ?? '')
            : new Date(khanoumiSubOrderShippingCost?.day ?? ''),
        shipping_discount: hasFreeShipping ? Number(khanoumiSubOrderShippingMethod?.shippingMethodCost) : 0,
    }

    if (khanoumiSubOrderShippingMethod?.shippingMethodTitle) {
        shippingInfo.shipping_tier = khanoumiSubOrderShippingMethod.shippingMethodTitle
    }

    sendWebengageEvent(SupportedEvents.add_shipping_info, shippingInfo)
}

export const trackPaymentInfo = (paymentMethod: GetPaymentOptionsViewModel) => {
    const paymentData = {
        wallet_credit: paymentMethod.credit ?? 0,
        ...(paymentMethod?.paymentMethodType ? { payment_type: paymentMethod?.paymentMethodType?.toLowerCase() } : {}),
    }
    sendWebengageEvent(SupportedEvents.add_payment_info, paymentData)
}

export const trackBeginCheckout = (cart: GetSubCartViewModel) => {
    const cartItems = cart.subCarts?.flatMap((subCart) => subCart.items || []) || []
    const sumOfQty = cart.subCarts?.reduce((acc, subCart) => acc + (subCart.sumOfQty ?? 0), 0) ?? 0
    const { coupon, coupon_amount } = generateEventsCouponData(cartItems ?? [], cart.promotion || null, null)

    const variantFields = generateProductVariants(null, null, null, cart.subCarts || null, [
        KeyPathType.atpId,
        KeyPathType.productName,
        KeyPathType.salesPrice,
        KeyPathType.discount,
        KeyPathType.brandName,
        KeyPathType.colorName,
        KeyPathType.categoryLevel1,
        KeyPathType.categoryLevel2,
        KeyPathType.categoryLevel3,
        KeyPathType.shipBySeller,
    ])

    const checkoutInfo = {
        value: cart.totalEffectivePrice ?? 0,
        overall_quantity: sumOfQty,
        item_id: variantFields[KeyPathType.atpId] ?? '',
        item_name: variantFields[KeyPathType.productName] ?? '',
        price: variantFields[KeyPathType.salesPrice],
        discount: variantFields[KeyPathType.discount],
        item_brand: variantFields[KeyPathType.brandName] ?? '',
        item_variant: variantFields[KeyPathType.colorName] ?? '',
        item_category1: variantFields[KeyPathType.categoryLevel1] ?? '',
        item_category2: variantFields[KeyPathType.categoryLevel2] ?? '',
        item_category3: variantFields[KeyPathType.categoryLevel3] ?? '',
        ship_by_seller: variantFields[KeyPathType.shipBySeller] ?? false,
        ...(coupon ? { coupon } : {}),
        ...(coupon_amount ? { coupon_amount: coupon_amount } : {}),
    }

    sendWebengageEvent(SupportedEvents.begin_checkout, checkoutInfo)
}

export const trackPurchase = (transactionData: PaymentFinalStatusViewModel, paymentId: string) => {
    const order = transactionData.order
    const subOrder = order?.subOrder
    const lineItems = subOrder?.lineItems ?? []

    const value = calculatePurchaseTotalValue(lineItems)
    const coupon = order?.promotionOfferType ? `${order.promotionOfferType}-${order.promotionTitle}` : undefined
    const couponAmount = order?.totalPromotionAmount !== undefined ? String(order.totalPromotionAmount) : undefined

    const formattedVariants = lineItems.map((item) => ({
        productName: item.product?.name ?? '',
        productId: item.product?.productId ?? '',
        salesPrice: item.price ?? 0,
        basePrice: item.basePrice ?? 0,
        discount: item.unitDiscount ?? 0,
        brandName: item.product?.brand ?? '',
        colorName: item.product?.color ?? '',
        categoryLevel1: item.product?.categoryLevels?.level1?.nameFa ?? '',
        categoryLevel2: item.product?.categoryLevels?.level2?.nameFa ?? '',
        categoryLevel3: item.product?.categoryLevels?.level3?.nameFa ?? '',
        variant: item.product?.color?.name ?? '',
        shipBySeller: item.shipBySeller ?? false,
    }))

    const variantFields = generateProductVariants(null, null, formattedVariants, null, [
        KeyPathType.productName,
        KeyPathType.productId,
        KeyPathType.salesPrice,
        KeyPathType.discount,
        KeyPathType.brandName,
        KeyPathType.colorName,
        KeyPathType.categoryLevel1,
        KeyPathType.categoryLevel2,
        KeyPathType.categoryLevel3,
        KeyPathType.itemVariant,
        KeyPathType.shipBySeller,
    ])

    const updatedTransactionData = {
        value,
        transaction_id: paymentId,
        order_id: order?.orderId ?? '',
        payment_type: transactionData.gateway ?? '',
        shipping_price: Number(order?.shippingCost ?? 0),
        item_id: variantFields[KeyPathType.productId] ?? '',
        item_name: variantFields[KeyPathType.productName] ?? '',
        price: variantFields[KeyPathType.salesPrice] ?? '',
        discount: variantFields[KeyPathType.discount] ?? '',
        item_brand: variantFields[KeyPathType.brandName] ?? '',
        item_category1: variantFields[KeyPathType.categoryLevel1] ?? '',
        item_category2: variantFields[KeyPathType.categoryLevel2] ?? '',
        item_category3: variantFields[KeyPathType.categoryLevel3] ?? '',
        ship_by_seller: variantFields[KeyPathType.shipBySeller] ?? false,
        ...(coupon && { coupon }),
        ...(couponAmount && { coupon_amount: Number(couponAmount) }),
        ...(variantFields[KeyPathType.itemVariant] !== 'Blank'
            ? { item_variant: variantFields[KeyPathType.itemVariant] }
            : {}),
    }

    sendWebengageEvent(SupportedEvents.purchase, updatedTransactionData)
}

export const trackCartAction = (
    selectedProduct: CartItemViewModel,
    placement: CartActionPlacement,
    actionType: SupportedEvents.add_to_cart | SupportedEvents.remove_from_cart,
    isGift: boolean
) => {
    const productData: CartProductType = {
        item_id: selectedProduct.atpId ?? '',
        item_name: productNameSizeHandler(selectedProduct.productName ?? ''),
        price: isGift ? 0 : (selectedProduct.salesPrice ?? 0),
        value: isGift ? 0 : (selectedProduct.salesPrice ?? 0),
        discount: isGift
            ? Number(selectedProduct.basePrice)
            : Number(selectedProduct.basePrice) - Number(selectedProduct.salesPrice),
        item_brand: selectedProduct.brandName ?? '',
        quantity: selectedProduct.qty ?? '',
        item_list_name: placement,
        item_list_id: placement,
        ship_by_seller: selectedProduct.shipBySeller ?? false,
        ...(isGift && { coupon: 'pricinggift' }),
        ...(selectedProduct.colorName && { item_variant: selectedProduct.colorName }),
        ...(selectedProduct.categoryLevel1 && { item_category1: selectedProduct.categoryLevel1 }),
        ...(selectedProduct.categoryLevel2 && { item_category2: selectedProduct.categoryLevel2 }),
        ...(selectedProduct.categoryLevel3 && { item_category3: selectedProduct.categoryLevel3 }),
    }

    sendWebengageEvent(actionType, productData)
}

export const trackCtaAction = ({
    banner_type,
    banner_name,
    banner_index,
    url_destination,
    item_id,
    component_id,
    image_url,
    term,
    elementGroupName,
}: ActionType) => {
    const updatedBannerData = {
        banner_type,
        banner_name,
        banner_index,
        item_id,
        component_id,
        image_url,
        page_path: window.location.href,
        destinationUrl: normalizeDestinationUrl(url_destination),
        ...(term && { term }),
        ...(elementGroupName && { elementGroupName }),
    }

    const filteredBannerData = Object.fromEntries(
        Object.entries(updatedBannerData).filter(([, value]) => value !== null && value !== '')
    )

    sendWebengageEvent(SupportedEvents.cta_click, filteredBannerData as ActionType)
}
